import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { CircleNotch, Rocket, Plus, Trash, Cpu, HardDrive, Users } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = '#14B8A6';

const RDPNewDeployment = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [meta, setMeta] = useState({ regions: [], sizes: [], images: [] });
  const [form, setForm] = useState({ account_id: '', region: 'eastus', vm_size: 'Standard_B2ms', os_image_key: 'ws2022', os_disk_gb: 128, admin_username: 'rdpadmin', name: '' });
  const [users, setUsers] = useState([]);
  const [quota, setQuota] = useState(null);
  const [price, setPrice] = useState(null);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/rdp/accounts`, { withCredentials: true }).then(r => {
      const ready = r.data.filter(a => a.status === 'ready');
      setAccounts(r.data);
      if (ready.length) setForm(f => ({ ...f, account_id: ready[0].id }));
    });
    axios.get(`${API}/api/rdp/meta`, { withCredentials: true }).then(r => setMeta(r.data));
  }, []);

  useEffect(() => {
    if (form.account_id && form.region) {
      axios.get(`${API}/api/rdp/accounts/${form.account_id}/quota?region=${form.region}`, { withCredentials: true })
        .then(r => setQuota(r.data.cores)).catch(() => setQuota(null));
    }
  }, [form.account_id, form.region]);

  useEffect(() => {
    if (form.region && form.vm_size) {
      setPrice(null);
      axios.get(`${API}/api/rdp/pricing?region=${form.region}&size=${form.vm_size}`, { withCredentials: true })
        .then(r => setPrice(r.data)).catch(() => setPrice(null));
    }
  }, [form.region, form.vm_size]);

  const sizeObj = meta.sizes.find(s => s.name === form.vm_size);

  const deploy = async () => {
    if (!form.account_id) return toast.error('Select an Azure account (must be tested/ready)');
    setDeploying(true);
    try {
      const body = { ...form, os_disk_gb: Number(form.os_disk_gb), extra_users: users.filter(u => u.username.trim()).map(u => ({ username: u.username.trim() })) };
      await axios.post(`${API}/api/rdp/vms`, body, { withCredentials: true });
      toast.success('Provisioning started — creating your Windows machine');
      navigate('/rdp/machines');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start'); }
    finally { setDeploying(false); }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500 max-w-4xl">
      <div>
        <h1 className="text-4xl font-black mb-2" style={{ color: T }}>NEW RDP MACHINE</h1>
        <p className="text-slate-400 font-mono">Provision a Windows Server on Azure with RDP enabled.</p>
      </div>

      {accounts.filter(a => a.status === 'ready').length === 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 p-4 text-sm">
          No tested Azure account. Add one under <button className="underline" onClick={() => navigate('/rdp/accounts')}>Azure Accounts</button> and hit Test first.
        </div>
      )}

      <div className="bg-card border border-white/10 p-6 space-y-5">
        {/* Account */}
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Azure Account</label>
          <select value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} data-testid="rdp-account-select"
            className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]">
            <option value="">-- choose --</option>
            {accounts.map(a => <option key={a.id} value={a.id} disabled={a.status !== 'ready'}>{a.label}{a.status !== 'ready' ? ' (not tested)' : ''}</option>)}
          </select>
        </div>

        {/* Region + Windows */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Region</label>
            <select value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} data-testid="rdp-region-select"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]">
              {meta.regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {quota && <div className="text-xs text-slate-500 mt-1 font-mono">Quota: {quota.current}/{quota.limit} vCPUs used</div>}
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Windows Version</label>
            <select value={form.os_image_key} onChange={e => setForm({ ...form, os_image_key: e.target.value })} data-testid="rdp-image-select"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]">
              {meta.images.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
            </select>
          </div>
        </div>

        {/* VM size cards */}
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Machine Size (CPU / RAM)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {meta.sizes.map(s => (
              <button key={s.name} onClick={() => setForm({ ...form, vm_size: s.name })} data-testid={`rdp-size-${s.name}`}
                className={`text-left p-3 border transition-colors ${form.vm_size === s.name ? 'border-[#14B8A6] bg-[#14B8A6]/10' : 'border-white/10 hover:border-white/30'}`}>
                <div className="flex items-center gap-2 font-bold text-white text-sm"><Cpu size={16} style={{ color: T }} /> {s.cpu} vCPU</div>
                <div className="text-xs text-slate-400 mt-1">{s.ram_gb} GB RAM · {s.tier}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{s.name}</div>
                {s.note === 'Recommended' && <div className="text-[10px] font-bold mt-1" style={{ color: T }}>★ {s.note}</div>}
              </button>
            ))}
          </div>
        </div>

        {/* disk + admin user + name */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">OS Disk (GB)</label>
            <input type="number" min="30" max="2048" value={form.os_disk_gb} onChange={e => setForm({ ...form, os_disk_gb: e.target.value })} data-testid="rdp-disk-input"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Admin Username</label>
            <input value={form.admin_username} onChange={e => setForm({ ...form, admin_username: e.target.value })} data-testid="rdp-admin-input"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block mb-2">Name (optional)</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="auto" data-testid="rdp-name-input"
              className="w-full bg-[#0A0B0E] border border-white/10 p-3 text-white focus:outline-none focus:border-[#14B8A6]" />
          </div>
        </div>
        <p className="text-xs text-slate-500 -mt-2">Admin password is auto-generated (shown after deploy). RDP (3389) opens to everyone (0.0.0.0/0).</p>

        {/* extra users */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2"><Users size={16} style={{ color: T }} /> Extra RDP Users (optional)</label>
            <button onClick={() => setUsers([...users, { username: '' }])} data-testid="rdp-add-user-row"
              className="flex items-center gap-1 text-xs border border-white/15 px-2 py-1 text-slate-300 hover:bg-white/5"><Plus size={14} /> Add user</button>
          </div>
          {users.map((u, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input value={u.username} onChange={e => { const n = [...users]; n[i].username = e.target.value; setUsers(n); }} placeholder="username" data-testid={`rdp-user-${i}`}
                className="flex-1 bg-[#0A0B0E] border border-white/10 p-2 text-white text-sm focus:outline-none focus:border-[#14B8A6]" />
              <button onClick={() => setUsers(users.filter((_, j) => j !== i))} className="text-red-400 px-2"><Trash size={16} /></button>
            </div>
          ))}
          <p className="text-[11px] text-slate-500">Each extra user gets an auto-generated password (shown on the machine page). Note: Windows Server allows 2 users connected at once by default.</p>
        </div>

        {/* cost */}
        <div className="bg-[#0A0B0E] border border-white/10 p-4 flex items-center justify-between">
          <div className="text-sm text-slate-400">Estimated cost {sizeObj ? `(${sizeObj.cpu} vCPU / ${sizeObj.ram_gb} GB)` : ''}</div>
          <div className="text-right">
            {price === null ? <CircleNotch size={16} className="animate-spin text-slate-500" /> : (
              <div>
                <div className="text-lg font-black" style={{ color: T }}>{price.windows_mo ? `$${price.windows_mo}/mo` : '—'}</div>
                <div className="text-xs text-slate-500 font-mono">{price.windows_hr ? `$${price.windows_hr}/hr VM` : ''} + ~$13/mo disk & IP</div>
              </div>
            )}
          </div>
        </div>

        <button onClick={deploy} disabled={deploying || !form.account_id} data-testid="rdp-deploy-btn"
          className="w-full flex items-center justify-center gap-2 text-black py-4 font-black uppercase tracking-wider disabled:opacity-40" style={{ background: T }}>
          {deploying ? <CircleNotch size={20} className="animate-spin" /> : <Rocket size={20} />} Provision Windows Machine
        </button>
      </div>
    </div>
  );
};

export default RDPNewDeployment;
